// Settings Page Configuration Constants

export type SettingsSection = 'modules' | 'inverters' | 'optimizers' | 'combiners' | 'rsds' | 'batteries' | 'structural' | 'msp' | 'poi' | 'disco' | 'conduit' | 'attachments' | 'rails' | 'mounting' | 'roofing' | 'building' | 'electric' | 'settings' | 'general-settings' | 'client-logos';

export interface SettingsSectionConfig {
  id: SettingsSection;
  label: string;
  icon: string;
  description: string;
  color: string;
  bgColor: string;
  route?: string;
  enabled: boolean;
}

// Settings section configurations
export const SETTINGS_SECTIONS_CONFIG: SettingsSectionConfig[] = [
  { 
    id: 'modules', 
    label: 'Solar Modules', 
    icon: 'solar', 
    description: 'Manage module brands, models, and specifications for solar installations',
    color: 'from-amber-500 to-orange-500',
    bgColor: 'from-amber-50 to-orange-50',
    enabled: true
  },
  { 
    id: 'inverters', 
    label: 'Inverters', 
    icon: 'inverter', 
    description: 'Configure inverter brands and their models',
    color: 'from-rose-500 to-pink-500',
    bgColor: 'from-rose-50 to-pink-50',
    enabled: true
  },
  { 
    id: 'optimizers', 
    label: 'Optimizers', 
    icon: 'optimizer', 
    description: 'Manage power optimizer models and compatibility settings',
    color: 'from-slate-600 to-slate-700',
    bgColor: 'from-slate-50 to-slate-100',
    enabled: true
  },
  {
    id: 'combiners',
    label: 'Combiners',
    icon: 'combiner',
    description: 'Manage combiner models linked to inverter brands',
    color: 'from-emerald-500 to-teal-500',
    bgColor: 'from-emerald-50 to-teal-50',
    enabled: true
  },
  {
    id: 'rsds',
    label: 'RSD Types',
    icon: 'rsd',
    description: 'Manage RSD types linked to inverter brands',
    color: 'from-violet-500 to-purple-500',
    bgColor: 'from-violet-50 to-purple-50',
    enabled: true
  },
  {
    id: 'batteries',
    label: 'Battery',
    icon: 'battery',
    description: 'Configure battery brands and models',
    color: 'from-orange-500 to-amber-600',
    bgColor: 'from-orange-50 to-amber-50',
    enabled: true
  },
  { 
    id: 'structural', 
    label: 'Structural', 
    icon: 'structural', 
    description: 'Manage framing specifications, materials, and mounting requirements',
    color: 'from-stone-600 to-amber-600',
    bgColor: 'from-stone-50 to-amber-50',
    enabled: true
  },
  { 
    id: 'msp', 
    label: 'MSP', 
    icon: 'msp', 
    description: 'Configure MSP types, manufacturers, and electrical specifications',
    color: 'from-amber-600 to-rose-600',
    bgColor: 'from-amber-50 to-rose-50',
    enabled: true
  },
  { 
    id: 'poi', 
    label: 'POI', 
    icon: 'poi', 
    description: 'Manage POI methods and locations',
    color: 'from-rose-600 to-orange-500',
    bgColor: 'from-rose-50 to-orange-50',
    enabled: true
  },
  { 
    id: 'disco', 
    label: 'DISCO', 
    icon: 'disco', 
    description: 'Configure disconnect switch models, ratings, types, and fuse specifications',
    color: 'from-orange-600 to-red-600',
    bgColor: 'from-orange-50 to-red-50',
    enabled: true
  },
  { 
    id: 'conduit', 
    label: 'Conduit', 
    icon: 'conduit', 
    description: 'Manage conduit sizes and types',
    color: 'from-slate-600 to-amber-600',
    bgColor: 'from-slate-50 to-amber-50',
    enabled: true
  },
  { 
    id: 'attachments', 
    label: 'Attachments', 
    icon: 'attachment', 
    description: 'Manage attachment methods, hardware, and mounting specifications',
    color: 'from-amber-600 to-rose-600',
    bgColor: 'from-amber-50 to-rose-50',
    enabled: false
  },
  { 
    id: 'rails', 
    label: 'Rails & Mounting', 
    icon: 'rail', 
    description: 'Configure rail types, lengths, and mounting system components',
    color: 'from-rose-600 to-orange-600',
    bgColor: 'from-rose-50 to-orange-50',
    enabled: false
  },
  { 
    id: 'mounting', 
    label: 'Attachments & Rails', 
    icon: 'mounting', 
    description: 'Manage attachments and rail systems with their relationships',
    color: 'from-amber-600 to-rose-600',
    bgColor: 'from-amber-50 to-rose-50',
    enabled: true
  },
  { 
    id: 'roofing', 
    label: 'Roofing & Reroof', 
    icon: 'roofing', 
    description: 'Manage roof types, conditions, and reroof status configurations',
    color: 'from-amber-600 to-orange-600',
    bgColor: 'from-amber-50 to-orange-50',
    enabled: true
  },
  { 
    id: 'building', 
    label: 'Building Attributes', 
    icon: 'building', 
    description: 'Configure building height classifications and structural attributes',
    color: 'from-slate-600 to-slate-700',
    bgColor: 'from-slate-50 to-slate-100',
    enabled: true
  },
  {
    id: 'electric',
    label: 'Electric Configuration',
    icon: 'electric',
    description: 'Manage electrical configurations, load calculations, and utility specifications',
    color: 'from-amber-600 to-orange-600',
    bgColor: 'from-amber-50 to-orange-50',
    enabled: true
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'settings',
    description: 'Configure application settings and calculation parameters',
    color: 'from-slate-600 to-slate-700',
    bgColor: 'from-slate-50 to-slate-100',
    enabled: false
  },
  {
    id: 'general-settings',
    label: 'General Settings',
    icon: 'gear',
    description: 'Configure application-wide settings and calculation parameters',
    color: 'from-slate-600 to-slate-700',
    bgColor: 'from-slate-50 to-slate-100',
    enabled: true
  },
  {
    id: 'client-logos',
    label: 'Client Logos',
    icon: 'document',
    description: 'Manage client name to logo mappings',
    color: 'from-amber-500 to-orange-500',
    bgColor: 'from-amber-50 to-orange-50',
    enabled: true
  }
];

// Helper function to get only enabled sections
export const getEnabledSettingsSections = () => 
  SETTINGS_SECTIONS_CONFIG.filter(section => section.enabled);

// Icon path mappings for admin sections
export const SETTINGS_ICON_PATHS = {
  solar: "M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z",
  inverter: "M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
  optimizer: "M13 10V3L4 14h7v7l9-11h-7z",
  combiner: "M4 6h16M4 10h16M4 14h16M4 18h16",
  rsd: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
  battery: "M19 10h-1V6a4 4 0 00-4-4H6a4 4 0 00-4 4v4H1a1 1 0 000 2h1v4a4 4 0 004 4h8a4 4 0 004-4v-4h1a1 1 0 100-2z",
  structural: "M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 10a1 1 0 011-1h6a1 1 0 011 1v8a1 1 0 01-1 1H5a1 1 0 01-1-1v-8zm10 0a1 1 0 011-1h4a1 1 0 011 1v8a1 1 0 01-1 1h-4a1 1 0 01-1-1v-8z",
  msp: "M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2",
  poi: "M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z",
  disco: "M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  conduit: "M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4",
  attachment: "M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13",
  rail: "M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4",
  mounting: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
  roofing: "M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L10 4.414l6.293 6.293a1 1 0 001.414-1.414l-7-7zM13 11v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6H1.5a.5.5 0 01-.354-.854L10 1.293a1 1 0 011.414 0l8.854 8.853A.5.5 0 0119.5 11H13z",
  building: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
  electric: "M13 10V3L4 14h7v7l9-11h-7z",
  settings: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z",
  gear: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z",
  plus: "M12 6v6m0 0v6m0-6h6m-6 0H6",
  document: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  menu: "M4 6h16M4 12h16M4 18h16",
  close: "M6 18L18 6M6 6l12 12"
} as const;
